////Users/bobbygilbert/Documents/GitHub/TPCTrust-platform/app/[locale]/(routes)/crm/opportunities/table-componentsB/columns.tsx

/* eslint-disable react-hooks/rules-of-hooks */
"use client";

import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Edit, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

import { statuses } from "../table-data/data";
import { Opportunity } from "../table-data/schema";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableRowActions } from "./data-table-row-actions";
import { CommentModal } from "./comment-modal";
import moment from "moment";
import Link from "next/link";

export const columns: ColumnDef<Opportunity>[] = [
  {
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    id: "actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => (
      <div className="w-[80px]">
        {moment(row.getValue("createdAt")).format("MM-DD-YY")}
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Deal Name" />
    ),
    cell: ({ row }) => (
      <Link href={`/crm/opportunities/${row.original.id}`}>
        <div className="w-[100px]">{row.getValue("name")}</div>
      </Link>
    ),
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "budget",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => {
      return (
        <div>
          {row.original.budget
            ? row.original.budget.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })
            : "N/A"}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  // Add the Advisor Name column
  {
    id: "advisorName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Advisor" />
    ),
    cell: ({ row }) => {
      // Access the advisor name from the assigned lead
      const advisorName = row.original.assigned_leads?.advisorName || 
                         row.original.assigned_leads?.assigned_to_user?.name || "N/A";
      
      return <div className="w-[120px]">{advisorName}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  // Add the Investor column (Lead's name)
  {
    id: "investor",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Investor" />
    ),
    cell: ({ row }) => {
      // Combine first name and last name from the assigned lead
      const firstName = row.original.assigned_leads?.firstName || "";
      const lastName = row.original.assigned_leads?.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim() || "N/A";
      
      return <div className="w-[120px]">{fullName}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  // Add the State column from the lead
  {
    id: "state",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="State" />
    ),
    cell: ({ row }) => {
      const state = row.original.assigned_leads?.state || "N/A";
      
      return <div className="w-[80px]">{state}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  // Add the Comments column with edit capability
  {
    accessorKey: "comments",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Comments" />
    ),
    cell: ({ row }) => {
      const [showModal, setShowModal] = useState(false);
      
      return (
        <div className="flex items-center gap-2">
          <div className="max-w-[200px] truncate">
            {row.original.comments || "No comments yet"}
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setShowModal(true)}
            className="w-8 h-8"
          >
            <Edit className="w-4 h-4" />
          </Button>
          
          {showModal && (
            <CommentModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              opportunityId={row.original.id}
              currentComment={row.original.comments}
              onSuccess={() => {
                // This will be handled by the parent component
                // to refresh the data after a successful update
              }}
            />
          )}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
];